import { useState, useCallback, useEffect, useRef } from "react";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";
import { useOcelMetadata } from "../../../../stores/OcelContext";
import { apiClient, getAvailableFiles } from "../../../../services/apiClient";
import { useVariantExplorerExtended } from "../../../variants/VariantExplorerExtended/hooks/useVariantExplorerExtended";

/**
 * Orchestrates all state for the VisualConformanceChecker.
 *
 * Phase transitions:
 *   setup → selecting (after reference upload + object type selected)
 *   selecting → results (after visual check completes)
 *   any → setup (on reset)
 */
export function useVisualConformance(activeFile) {
  const api = useGlobalAPI(activeFile);
  const metadata = useOcelMetadata(activeFile);
  const objectTypes = metadata?.object_types ? Object.keys(metadata.object_types) : [];

  // Incremented on every runVisualCheck call; used to discard stale in-flight responses.
  const runIdRef = useRef(0);

  // ── Phase ────────────────────────────────────────────────────────────────
  const [phase, setPhase] = useState("setup"); // 'setup' | 'selecting' | 'results'

  // ── Setup state (mirrors useConformanceChecker) ───────────────────────────
  const [objectType, setObjectType] = useState("");
  const [zeta, setZeta] = useState(2.0);
  const [profilingMode, setProfilingMode] = useState("standard");
  const [referenceFile, setReferenceFile] = useState(null);
  const [referenceKey, setReferenceKey] = useState(null);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [showFilePicker, setShowFilePicker] = useState(false);

  // ── Reference variants (fetched from /conformance/reference-variants) ─────
  const [referenceVariants, setReferenceVariants] = useState([]);
  const [referenceVariantsMeta, setReferenceVariantsMeta] = useState(null);
  const [selectedReferenceIds, setSelectedReferenceIds] = useState([]);
  const [loadingRefVariants, setLoadingRefVariants] = useState(false);

  // ── Observed variants (delegated to the existing hook) ───────────────────
  // Only active (passes objectType) after we move to selecting phase.
  const observedHook = useVariantExplorerExtended(
    phase === "selecting" || phase === "results" ? objectType : null,
    activeFile
  );

  // selectedObservedIds = the hook's own selectedVariants (fully managed there)
  const selectedObservedIds = observedHook.selectedVariants;
  const onToggleObserved = observedHook.handleToggleSelect;

  // ── Visual check result ───────────────────────────────────────────────────
  const [visualResult, setVisualResult] = useState(null);
  const [sequentialResult, setSequentialResult] = useState(null);
  const [temporalResult, setTemporalResult] = useState(null);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [loadingTemporal, setLoadingTemporal] = useState(false);
  const [checkError, setCheckError] = useState(null);

  // Fetch the File Manager's file list once, for the "select existing" reference picker.
  useEffect(() => {
    getAvailableFiles()
      .then((files) => setAvailableFiles(files ?? []))
      .catch((err) => console.error("Failed to load available files for reference picker:", err));
  }, []);

  // Reset when active file changes
  useEffect(() => {
    setPhase("setup");
    setObjectType("");
    setReferenceFile(null);
    setReferenceKey(null);
    setUploadError(null);
    setReferenceVariants([]);
    setReferenceVariantsMeta(null);
    setSelectedReferenceIds([]);
    setVisualResult(null);
    setSequentialResult(null);
    setTemporalResult(null);
    setCheckError(null);
    setZeta(2.0);
    setProfilingMode("standard");
  }, [activeFile]);

  // ── Reference file upload (identical logic to useConformanceChecker) ──────
  const handleReferenceFileChange = useCallback(async (file) => {
    setReferenceFile(file);
    setReferenceKey(null);
    setUploadError(null);
    if (!file) return;

    setUploadingRef(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiClient.post(
        "/conformance/upload-reference",
        formData,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }
      );
      setReferenceKey(response.data.reference_key);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Upload failed";
      setUploadError(msg);
      setReferenceFile(null);
    } finally {
      setUploadingRef(false);
    }
  }, []);

  // ── Use an already-uploaded File Manager file as the reference ────────────
  const handleReferenceFromExisting = useCallback(async (file) => {
    setReferenceFile({ name: file.display_name || file.file_type });
    setReferenceKey(null);
    setUploadError(null);

    setUploadingRef(true);
    try {
      const response = await apiClient.post("/conformance/reference-from-file", {
        file_type: file.file_type,
      });
      setReferenceKey(response.data.reference_key);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Failed to load reference";
      setUploadError(msg);
      setReferenceFile(null);
    } finally {
      setUploadingRef(false);
    }
  }, []);

  // ── Reference file picker (existing File Manager files) ───────────────────
  const openFilePicker = useCallback(() => setShowFilePicker(true), []);
  const closeFilePicker = useCallback(() => setShowFilePicker(false), []);
  const handleSelectExistingFile = useCallback((file) => {
    setShowFilePicker(false);
    return handleReferenceFromExisting(file);
  }, [handleReferenceFromExisting]);

  // ── Start selecting: fetch reference variants then open modal ─────────────
  const startSelecting = useCallback(async () => {
    if (!objectType || !referenceKey) return;

    setLoadingRefVariants(true);
    setCheckError(null);
    try {
      const response = await apiClient.post("/conformance/reference-variants", {
        reference_key: referenceKey,
        object_type: objectType,
      });
      const data = response.data;
      setReferenceVariants(data.variants ?? []);
      setReferenceVariantsMeta({
        summary: data.summary,
        activity_catalog: data.activity_catalog,
      });
      // Pre-select all reference variants
      setSelectedReferenceIds((data.variants ?? []).map((v) => v.variant_id));
      setPhase("selecting");
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || "Failed to load reference variants";
      setCheckError(msg);
    } finally {
      setLoadingRefVariants(false);
    }
  }, [objectType, referenceKey]);

  // ── Toggle reference variant selection ────────────────────────────────────
  const onToggleReference = useCallback((variantId) => {
    setSelectedReferenceIds((prev) =>
      prev.includes(variantId)
        ? prev.filter((id) => id !== variantId)
        : [...prev, variantId]
    );
  }, []);

  // ── Run visual conformance check ──────────────────────────────────────────
  const runVisualCheck = useCallback(async () => {
    if (selectedReferenceIds.length === 0 || selectedObservedIds.length === 0) return;

    // Capture a monotonically increasing run ID before any await so that if the
    // user triggers a second run while this one is in-flight, the stale response
    // is discarded and state is never written with outdated results.
    const runId = ++runIdRef.current;
    setLoadingCheck(true);
    setCheckError(null);
    try {
      const [visualRes, seqRes, tempRes] = await Promise.allSettled([
        api.post(
          "/conformance/visual-check",
          {
            reference_key: referenceKey,
            object_type: objectType,
            selected_reference_variant_ids: selectedReferenceIds,
            selected_observed_variant_ids: selectedObservedIds,
          },
          {},
          "Visual Conformance Check"
        ),
        api.post(
          "/conformance/check",
          {
            object_type: objectType,
            reference_key: referenceKey,
            selected_observed_variant_ids: selectedObservedIds,
            selected_reference_variant_ids: selectedReferenceIds,
          },
          {},
          "Sequential Conformance Check"
        ),
        api.post(
          "/conformance/check-temporal",
          { object_type: objectType, reference_key: referenceKey, zeta, profiling_mode: profilingMode, selected_observed_variant_ids: selectedObservedIds },
          {},
          "Temporal Conformance Check"
        ),
      ]);

      if (runId !== runIdRef.current) return; // superseded by a newer run

      if (visualRes.status === "rejected") {
        throw new Error(visualRes.reason?.message ?? "Visual check failed");
      }
      setVisualResult(visualRes.value);
      if (seqRes.status === "fulfilled") setSequentialResult(seqRes.value);
      if (tempRes.status === "fulfilled") setTemporalResult(tempRes.value);
      setPhase("results");
    } catch (err) {
      if (runId !== runIdRef.current) return;
      const msg =
        err.response?.data?.detail || err.message || "Visual check failed";
      setCheckError(msg);
    } finally {
      if (runId === runIdRef.current) setLoadingCheck(false);
    }
  }, [api, referenceKey, objectType, selectedReferenceIds, selectedObservedIds, zeta, profilingMode]);

  // ── Re-run only the temporal check (called when profiling mode or zeta changes in results view) ──
  const rerunTemporal = useCallback(async (newZeta, newProfilingMode) => {
    if (!referenceKey || !objectType || selectedObservedIds.length === 0) return;
    setLoadingTemporal(true);
    try {
      const result = await api.post(
        "/conformance/check-temporal",
        { object_type: objectType, reference_key: referenceKey, zeta: newZeta, profiling_mode: newProfilingMode, selected_observed_variant_ids: selectedObservedIds },
        {},
        "Temporal Conformance Check"
      );
      setTemporalResult(result);
    } catch (_) {
      // temporal failure is non-critical; keep stale result visible
    } finally {
      setLoadingTemporal(false);
    }
  }, [api, referenceKey, objectType, selectedObservedIds]);

  // ── Reset to setup ────────────────────────────────────────────────────────
  const reset = useCallback(() => {
    runIdRef.current = 0; // discard any in-flight run so its response is ignored
    setPhase("setup");
    setVisualResult(null);
    setSequentialResult(null);
    setTemporalResult(null);
    setCheckError(null);
    setReferenceVariants([]);
    setReferenceVariantsMeta(null);
    setSelectedReferenceIds([]);
  }, []);

  return {
    phase,
    // Setup
    objectType,
    setObjectType,
    objectTypes,
    zeta,
    setZeta,
    profilingMode,
    setProfilingMode,
    referenceFile,
    referenceKey,
    uploadingRef,
    uploadError,
    handleReferenceFileChange,
    availableFiles,
    handleReferenceFromExisting,
    showFilePicker,
    openFilePicker,
    closeFilePicker,
    handleSelectExistingFile,
    startSelecting,
    loadingRefVariants,
    // Selection
    referenceVariants,
    referenceVariantsMeta,
    selectedReferenceIds,
    onToggleReference,
    observedHook,
    selectedObservedIds,
    onToggleObserved,
    // Check
    runVisualCheck,
    rerunTemporal,
    loadingCheck,
    loadingTemporal,
    checkError,
    // Results
    visualResult,
    sequentialResult,
    temporalResult,
    reset,
  };
}
